import { isWasteOnly } from '@/config/deployment'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Home, TicketCheck, Users2, BookOpen,
  Building2, Receipt, Package, ScrollText, ShoppingCart,
  Settings, Settings2, Shield, HelpCircle, ChevronDown,
  ChevronsLeft, ChevronsRight, Palette, BarChart3, LayoutGrid,
  ScrollText as AuditIcon, CreditCard, KeyRound,
  FileText, FileCheck, ClipboardList, Layers, MessageSquare, CalendarClock, NotebookPen, Clock, Workflow,
  Sparkles, Server, Bell, Recycle, Warehouse, MapPin, Smartphone, Landmark, FileBarChart, Truck, Gavel, Factory,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAppStore } from '@/store/app'
import { useRole } from '@/hooks/useRole'
import { isSubscribedPath } from '@/utils/permissions'

interface NavItem {
  id: string
  label: string
  icon: React.ElementType
  path?: string
  badge?: string | number
  children?: { label: string; path: string; icon?: React.ElementType }[]
}

const NAV_ITEMS: NavItem[] = [
  { id: 'home', label: 'Home', icon: Home, path: '/' },
  {
    id: 'work-management', label: 'Work Management', icon: Layers,
    children: [
      { label: 'Task Board', path: '/tasks', icon: Layers },
      { label: 'Planning', path: '/tasks/planning', icon: CalendarClock },
      { label: 'Notes', path: '/notes', icon: NotebookPen },
      { label: 'Timesheets', path: '/tasks/timesheets', icon: Clock },
      { label: 'Reports', path: '/reports', icon: BarChart3 },
    ],
  },
  {
    id: 'helpdesk', label: 'Helpdesk', icon: TicketCheck,
    children: [
      { label: 'Tickets',        path: '/tickets',            icon: TicketCheck },
      { label: 'Live Chat',      path: '/tickets/live-chat',  icon: MessageSquare },
      { label: 'Scheduled',      path: '/tickets/scheduled',  icon: CalendarClock },
      { label: 'Analytics',      path: '/tickets/analytics',  icon: BarChart3   },
      { label: 'Clients',        path: '/clients',            icon: Users2      },
      { label: 'Knowledge Base', path: '/kb',                 icon: BookOpen    },
      { label: 'Ticket Settings',path: '/tickets/settings',   icon: Settings2   },
    ],
  },
  {
    id: 'assets', label: 'Assets', icon: Package,
    children: [
      { label: 'Asset List',      path: '/assets',           icon: Package    },
      { label: 'Purchase Orders', path: '/assets/purchase-orders',  icon: ClipboardList },
      { label: 'Analytics',       path: '/assets/analytics', icon: BarChart3  },
      { label: 'Agents',          path: '/assets/agents',    icon: Server     },
      { label: 'Contracts',       path: '/assets/contracts', icon: ScrollText },
      { label: 'Asset Settings',  path: '/assets/notifications', icon: Settings2 },
    ],
  },
  {
    id: 'waste', label: 'Waste Management', icon: Recycle,
    children: [
      { label: 'Control Tower',      path: '/waste',              icon: BarChart3   },
      { label: 'Collections',        path: '/waste/collections',  icon: Warehouse   },
      { label: 'Pickups',            path: '/waste/pickups',      icon: Truck       },
      { label: 'Oversight',          path: '/waste/oversight',    icon: Landmark    },
      { label: 'Reports',            path: '/waste/reports',      icon: FileBarChart },
      { label: 'MCF Facilities',     path: '/waste/facilities',   icon: MapPin      },
      { label: 'Panchayat',          path: '/waste/facilitators', icon: Users2      },
      { label: 'Agencies',           path: '/waste/agencies',     icon: Truck       },
      { label: 'Marketplace',        path: '/waste/marketplace',  icon: Gavel       },
      { label: 'Recyclers',          path: '/waste/recyclers',    icon: Factory     },
      { label: 'WhatsApp Simulator', path: '/waste/simulator',    icon: Smartphone  },
      { label: 'Waste Settings',     path: '/waste/settings',     icon: Settings2   },
    ],
  },
  {
    id: 'business', label: 'Business', icon: Building2,
    children: [
      { label: 'Quotations',        path: '/quotations',        icon: FileText     },
      { label: 'Proforma Invoices', path: '/proforma-invoices', icon: FileCheck    },
      { label: 'Sales Orders',      path: '/sales-orders',      icon: ShoppingCart },
      { label: 'Purchase Orders →', path: '/purchase-orders',   icon: ClipboardList},
      { label: 'Invoices',          path: '/invoices',          icon: Receipt      },
      { label: 'Invoice Settings',  path: '/invoices/settings', icon: Settings2    },
      { label: 'Process Workflows', path: '/process-workflows', icon: Workflow     },
    ],
  },
]

const ADMIN_ITEMS: NavItem[] = [
  {
    id: 'admin', label: 'Admin', icon: Shield,
    children: [
      { label: 'Organization Hub',    path: '/admin/organization', icon: Building2 },
      { label: 'Audit Logs',          path: '/admin/audit',    icon: AuditIcon },
      { label: 'Billing',             path: '/admin/billing',  icon: CreditCard },
      { label: 'Enterprise Auth',     path: '/admin/auth',        icon: KeyRound },
      { label: 'Branding',            path: '/admin/branding',    icon: Palette },
      { label: 'AI Provider',         path: '/admin/ai-provider', icon: Sparkles },
    ],
  },
]

// Returns the path of the single child that should be highlighted for the
// current location (pathname + search). A child with a query string only
// matches exactly; otherwise it matches its own path and anything beneath it.
// When several children match — /tickets and /tickets/settings both match
// /tickets/settings — the most specific (longest) one wins, so a parent route
// like Tickets doesn't stay lit while you're on one of its sibling pages.
function findActiveChildPath(children: NavItem["children"], currentPath: string): string | undefined {
  let best: string | undefined
  for (const child of children ?? []) {
    const target = child.path ?? ''
    const base = target.split('?')[0]
    if (!base) continue
    const matches = target.includes('?')
      ? currentPath === target
      : currentPath === base || currentPath.startsWith(base + '/') || currentPath.startsWith(base + '?')
    if (matches && (!best || target.length > best.length)) best = target
  }
  return best
}

// ── Collapsed flyout ──────────────────────────────────────────────────────────

function CollapsedPopover({
  item, anchorEl, onClose, onKeepOpen, currentPath,
}: {
  item: NavItem; anchorEl: HTMLElement
  onClose: () => void; onKeepOpen: () => void; currentPath: string
}) {
  const rect = anchorEl.getBoundingClientRect()
  const activeChildPath = findActiveChildPath(item.children, currentPath)
  return createPortal(
    <div
      className="fixed z-[200]"
      style={{ top: rect.top, left: rect.right + 6 }}
      onMouseEnter={onKeepOpen}
      onMouseLeave={onClose}
    >
      <div className="absolute right-full top-0 bottom-0 w-2" onMouseEnter={onKeepOpen} />
      <motion.div
        initial={{ opacity: 0, x: -6, scale: 0.97 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: -4, scale: 0.97 }}
        transition={{ duration: 0.12 }}
        className="bg-[rgb(var(--sidebar-bg))] border border-white/10 rounded-xl shadow-2xl py-1.5 min-w-[168px]"
      >
        <div className="px-3 py-1.5 mb-0.5">
          <span className="text-[11px] font-semibold text-white/40 uppercase tracking-wider">
            {item.label}
          </span>
        </div>
        {item.children?.map(child => {
          const active = child.path === activeChildPath
          const ChildIcon = child.icon
          return (
            <Link
              key={child.path}
              to={child.path ?? '/'}
              onClick={onClose}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 text-sm transition-colors mx-1 rounded-lg',
                active
                  ? 'bg-brand-500/20 text-white'
                  : 'text-[rgb(var(--sidebar-text))] hover:bg-white/8 hover:text-white',
              )}
            >
              {ChildIcon && <ChildIcon className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />}
              {child.label}
            </Link>
          )
        })}
      </motion.div>
    </div>,
    document.body,
  )
}

function CollapsedLabelTooltip({ label, anchorEl, onClose }: {
  label: string; anchorEl: HTMLElement; onClose: () => void
}) {
  const rect = anchorEl.getBoundingClientRect()
  return createPortal(
    <div
      className="fixed z-[200] pointer-events-none"
      style={{ top: rect.top + rect.height / 2 - 14, left: rect.right + 8 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -4 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -4 }}
        transition={{ duration: 0.1 }}
        className="bg-[rgb(var(--sidebar-bg))] border border-white/10 rounded-lg shadow-xl px-3 py-1.5 text-sm text-white whitespace-nowrap"
      >
        {label}
      </motion.div>
    </div>,
    document.body,
  )
}

// ── Main Sidebar ──────────────────────────────────────────────────────────────

export function Sidebar() {
  const location = useLocation()
  const { sidebarCollapsed, toggleSidebar, branding, user } = useAppStore()
  const { canAccessPath, isWasteRestricted } = useRole()
  const productName = branding.productName || 'MultiAccess'
  const logoUrl     = branding.logoUrl

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    helpdesk: true,
    business: false,
    admin: true,
  })
  const [flyout, setFlyout] = useState<{ item: NavItem; anchor: HTMLElement } | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openFlyout = useCallback((item: NavItem, anchor: HTMLElement) => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setFlyout({ item, anchor })
  }, [])
  const keepFlyoutOpen = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])
  const closeFlyout = useCallback(() => {
    closeTimer.current = setTimeout(() => setFlyout(null), 150)
  }, [])

  const toggleGroup = (id: string) =>
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }))

  const routeBase = (path?: string) => (path ?? '').split('?')[0]
  const isRouteMatch = (path?: string) => {
    const base = routeBase(path)
    if (!base) return false
    if (base === '/tasks') return location.pathname === base
    return location.pathname === base || location.pathname.startsWith(base + '/')
  }
  const isActive      = (path?: string) => path ? isRouteMatch(path) : false
  const isGroupActive = (item: NavItem) =>
    item.children?.some(c => isRouteMatch(c.path)) ?? false

  useEffect(() => { if (!sidebarCollapsed) setFlyout(null) }, [sidebarCollapsed])
  useEffect(() => {
    const activeGroup = [...NAV_ITEMS, ...ADMIN_ITEMS].find(item =>
      item.children?.some(child => isRouteMatch(child.path)),
    )
    if (activeGroup) setExpandedGroups(prev => ({ ...prev, [activeGroup.id]: true }))
  }, [location.pathname])

  const isSubscribed = (path?: string) => isSubscribedPath(path ?? '/', user?.roleName, user?.subscribedServices)

  const allNav = [...NAV_ITEMS, ...ADMIN_ITEMS]
    .map(item => item.children
      ? { ...item, children: item.children.filter(child => canAccessPath(child.path) && isSubscribed(child.path)) }
      : item)
    .filter(item => item.children ? item.children.length > 0 : canAccessPath(item.path ?? '/') && isSubscribed(item.path))

  return (
    <>
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 56 : 240 }}
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        className="flex flex-col h-screen bg-[rgb(var(--sidebar-bg))] border-r border-white/5 overflow-hidden flex-shrink-0 z-30"
      >
        {/* Logo */}
        <div className={cn("flex items-center px-3 border-b border-white/5", isWasteOnly && !sidebarCollapsed ? "h-32 shrink-0 justify-center" : "h-14")}>
          <div className="flex items-center gap-2 min-w-0">
            <img src={logoUrl || '/logo.png'} alt={productName} className={cn("object-contain flex-shrink-0", isWasteOnly && !sidebarCollapsed ? "w-28 h-28" : "w-8 h-8")} />
            <AnimatePresence>
              {!sidebarCollapsed && !isWasteOnly && (
                <motion.div
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  transition={{ duration: 0.15 }}
                  className="min-w-0"
                >
                  <p className="font-semibold text-white text-sm truncate leading-tight">{productName}</p>
                  <p className="text-[10px] text-white/40 tracking-wide">Business Platform</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {!sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="ml-auto p-1 rounded text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-0.5 px-2">
          {allNav.map((item, idx) => {
            const isAdminItem = ADMIN_ITEMS.some(a => a.id === item.id)
            return (
              <div key={item.id}>
                {isAdminItem && idx > 0 && (
                  <div className="border-t border-white/5 my-2" />
                )}
                <NavGroup
                  item={item}
                  collapsed={sidebarCollapsed}
                  expanded={!!expandedGroups[item.id]}
                  isActive={isActive(item.path)}
                  isGroupActive={isGroupActive(item)}
                  onToggle={() => toggleGroup(item.id)}
                  currentPath={`${location.pathname}${location.search}`}
                  onHover={openFlyout}
                  onLeave={closeFlyout}
                  flyoutOpen={flyout?.item.id === item.id}
                />
              </div>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/5 p-2 space-y-0.5">
          {!isWasteRestricted && <SidebarButton icon={HelpCircle} label="Help"     collapsed={sidebarCollapsed} to="/help"     onHover={openFlyout} onLeave={closeFlyout} />}
          {!isWasteRestricted && <SidebarButton icon={Settings}   label="Settings" collapsed={sidebarCollapsed} to="/settings" onHover={openFlyout} onLeave={closeFlyout} />}
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="w-full flex items-center justify-center p-2 rounded text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.aside>

      <AnimatePresence>
        {flyout && sidebarCollapsed && (
          flyout.item.children ? (
            <CollapsedPopover
              key={flyout.item.id}
              item={flyout.item}
              anchorEl={flyout.anchor}
              onClose={closeFlyout}
              onKeepOpen={keepFlyoutOpen}
              currentPath={`${location.pathname}${location.search}`}
            />
          ) : (
            <CollapsedLabelTooltip
              key={flyout.item.id}
              label={flyout.item.label}
              anchorEl={flyout.anchor}
              onClose={() => setFlyout(null)}
            />
          )
        )}
      </AnimatePresence>
    </>
  )
}

// ── NavGroup ──────────────────────────────────────────────────────────────────

function NavGroup({
  item, collapsed, expanded, isActive, isGroupActive,
  onToggle, currentPath, onHover, onLeave, flyoutOpen,
}: {
  item: NavItem; collapsed: boolean; expanded: boolean
  isActive: boolean; isGroupActive: boolean; onToggle: () => void
  currentPath: string
  onHover: (item: NavItem, anchor: HTMLElement) => void
  onLeave: () => void; flyoutOpen: boolean
}) {
  const Icon   = item.icon
  const rowRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null)
  const activeChildPath = findActiveChildPath(item.children, currentPath)

  const handleMouseEnter = () => {
    if (collapsed && rowRef.current) onHover(item, rowRef.current)
  }

  if (!item.children) {
    return (
      <Link
        ref={rowRef as any}
        to={item.path ?? '/'}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onLeave}
        className={cn(
          'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors',
          collapsed && 'justify-center',
          isActive
            ? 'bg-[rgb(var(--brand-500))]/20 text-white'
            : 'text-[rgb(var(--sidebar-text))] hover:bg-[rgb(var(--sidebar-hover))] hover:text-white',
          collapsed && flyoutOpen && 'bg-[rgb(var(--sidebar-hover))] text-white',
        )}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <AnimatePresence>
          {!collapsed && (
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 truncate">
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
        {!collapsed && item.badge && (
          <span className="text-[10px] bg-[rgb(var(--brand-500))] text-white px-1.5 py-0.5 rounded-full font-medium">
            {item.badge}
          </span>
        )}
      </Link>
    )
  }

  return (
    <div>
      <button
        ref={rowRef as any}
        onClick={collapsed ? undefined : onToggle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onLeave}
        className={cn(
          'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors',
          collapsed && 'justify-center',
          isGroupActive ? 'text-white' : 'text-[rgb(var(--sidebar-text))] hover:bg-[rgb(var(--sidebar-hover))] hover:text-white',
          collapsed && flyoutOpen && 'bg-[rgb(var(--sidebar-hover))] text-white',
        )}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <AnimatePresence>
          {!collapsed && (
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 text-left truncate">
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
        {!collapsed && (
          <ChevronDown className={cn('w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200', expanded && 'rotate-180')} />
        )}
      </button>

      <AnimatePresence>
        {!collapsed && expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden ml-3 mt-0.5 border-l border-white/10 pl-2 space-y-0.5"
          >
            {item.children?.map(child => {
              const active = child.path === activeChildPath
              const ChildIcon = child.icon
              return (
                <Link
                  key={child.path}
                  to={child.path ?? '/'}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors',
                    active
                      ? 'text-brand-300 bg-brand-500/20 font-medium'
                      : 'text-[rgb(var(--sidebar-text))] hover:text-white hover:bg-white/8',
                  )}
                >
                  {ChildIcon && <ChildIcon className={cn('w-3.5 h-3.5 flex-shrink-0', active ? 'opacity-100' : 'opacity-60')} />}
                  {child.label}
                </Link>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── SidebarButton ─────────────────────────────────────────────────────────────

function SidebarButton({
  icon: Icon, label, collapsed, to, onHover, onLeave,
}: {
  icon: React.ElementType; label: string; collapsed: boolean
  to: string
  onHover: (item: NavItem, anchor: HTMLElement) => void
  onLeave: () => void
}) {
  const ref = useRef<HTMLAnchorElement>(null)
  const syntheticItem: NavItem = { id: label, label, icon: Icon, path: to }
  const handleMouseEnter = () => {
    if (collapsed && ref.current) onHover(syntheticItem, ref.current)
  }
  return (
    <Link
      ref={ref}
      to={to}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onLeave}
      className={cn(
        'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm text-[rgb(var(--sidebar-text))] hover:bg-[rgb(var(--sidebar-hover))] hover:text-white transition-colors',
        collapsed && 'justify-center',
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <AnimatePresence>
        {!collapsed && (
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="truncate">
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </Link>
  )
}
